import type { ServerActionError } from '../types';

/**
 * Type definition for Server Action Middleware
 *
 * A middleware can either:
 * - Return an updated context that gets merged with the existing context
 * - Return an error that stops the action execution
 */
export type ServerActionMiddleware<Context, Input = unknown> = (params: {
  context: Context;
  input: Input;
}) => Promise<{ context: Context } | { error: ServerActionError }>;

/**
 * Helper for creating middleware with correct type inference for the output context.
 *
 * This helper solves the TypeScript type inference issue where factory functions
 * that return middleware lose the context type information. By wrapping your
 * middleware with this helper, you explicitly declare what context type the
 * middleware will output, enabling proper type inference through the `.use()` chain.
 *
 * @template TOutputContext - The type of context this middleware will output
 * @template TInput - The type of input data (defaults to unknown)
 *
 * @param middlewareFn - The middleware function that transforms context
 * @returns A typed middleware function with proper type inference
 *
 * @example
 * ```typescript
 * interface AuthContext {
 *   userId: string;
 *   role: string;
 * }
 *
 * const createAuthMiddleware = () =>
 *   createTypedMiddleware<AuthContext>(async ({ context, input }) => {
 *     const session = await getSession();
 *
 *     if (!session) {
 *       return { error: { message: 'Unauthorized', code: 'AUTH_ERROR' } };
 *     }
 *
 *     return {
 *       context: {
 *         userId: session.userId,
 *         role: session.role,
 *       },
 *     };
 *   });
 *
 * // Now TypeScript correctly infers authClient has AuthContext
 * const authClient = baseClient.use(createAuthMiddleware());
 *
 * // And in your actions, context is properly typed
 * authClient.action(async (context) => {
 *   const userId = context.userId; // ✅ Type-safe!
 * });
 * ```
 */
export function createTypedMiddleware<TOutputContext extends object, TInput = unknown>(
  middlewareFn: (params: {
    context: any;
    input: TInput;
  }) => Promise<{ context: TOutputContext } | { error: ServerActionError }>
): ServerActionMiddleware<TOutputContext, TInput> {
  return middlewareFn as any;
}
